import chains from "@sablier/v2-constants/chains";
import { StreamCategory } from "@sablier/v2-constants/stream";
import type { PeripheryCategory } from "@sablier/v2-constants";

/**
 * The official set of contracts used and recognized by the app.
 * -------------------------------------------------------------
 *
 * The contracts have been deployed from the following commits:
 *
 * | Repo         | Commit  |
 * | ------------ | ------- |
 * | v2-core      | https://github.com/sablier-labs/v2-core/tree/133f878 |
 * | v2-periphery | https://github.com/sablier-labs/v2-periphery/tree/cc9434f |
 * | prb-proxy    | https://github.com/PaulRBerg/prb-proxy/tree/6fbb6df |
 *
 * -------------------------------------------------------------
 *
 * Rules and Naming Conventions
 * ----------------------------
 *
 * 1. The default contracts used by the app will be labeled with the official stream category as names
 * 2. Addresses will be saved in lower-case format
 * 3. Aliases should not use "-" (hyphen)
 * 4. Flash loan contracts will be aliased with a suffixed "F" and need to be handled in the `contractor` utility
 * 5. Flash loan contracts will use keys derived from the base category and the Flash suffix: `${StreamCategory.<LINEAR or DYNAMIC>}Flash`
 *
 * Notes
 * ----------------------------
 * The "Withdrawer Target" is currently sourced from https://github.com/PaulRBerg/prb-proxy/blob/ae45af597a177647a3be28710d18ac62b769fed6/src/targetWithdrawer.sol
 */

const contracts: Record<
  number,
  Record<
    StreamCategory | string,
    { alias: string; address: string; methods?: Record<string, number> }
  >
> = {
  [chains.mainnet.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0xb10daee1fcf62243ae27776d7a92d39dc8740f95",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0x39efdc3dbb57b2388ccc4bb40ac4cb1226bc9e44",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.goerli.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x6e3678c005815ab34986d8d66a353cd3699103de",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0x4be70ede968e9dba12db42b9869bec66bedc17d7",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.polygon.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x67422c3e36a908d5c3237e9cffeb40bde7060f6e",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0x7313addb53f96a4f710d3b91645c62b434190725",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.arbitrum.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x197d655f3be03903fd25e7828c3534504bfe525e",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0xa9efbef1a35ff80041f567391bdc9813b2d50197",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.avalanche.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x610346e9088afa70d6b03e96a800b3267e75ca19",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0x665d1c8337f1035cfbe13dd94bb669110b975f5f",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.gnosis.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x685e92c9ca2bb23f1b596d0a7d749c0603e88585",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0xeb148e4ec13aaa65328c0ba089a278138e9e53f9",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.bsc.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x3fe4333f62a75c2a85c8211c6aefd1b9bfde6e51",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0xf2f3fef2454dca59eca929d2d8cd2a8669cc6214",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.optimism.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0xb923abdca17aed90eb5ec5e407bd37164f632bfd",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0x6f68516c21e248cddfaf4898e66b2b0adee0e0d6",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.base.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x6b9a46c8377f21517e65fa3899b3a9fab19d17f5",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0x645b00960dc352e699f89a81fc845c0c645231cf",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
  [chains.scroll.chainId]: {
    [StreamCategory.LOCKUP_LINEAR]: {
      alias: "LL", // SablierV2LockupLinear
      address: "0x80640ca758615ee83801ec43452feea09a202d33",
      methods: {
        withdraw: 250_000,
        cancel: 250_000,
      },
    },
    [StreamCategory.LOCKUP_DYNAMIC]: {
      alias: "LD", // SablierV2LockupDynamic
      address: "0xde6a30d851efd0fc2a9c922f294801cfd5fcb3a1",
      methods: {
        withdraw: 800_000,
        cancel: 250_000,
      },
    },
  },
} as const;

const peripheries: Record<
  number,
  Record<
    PeripheryCategory,
    {
      address: string;
      alias: string;
      methods?: Record<string, number | ((i: number) => number)>;
    }
  >
> = {
  [chains.mainnet.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0x9bdebf4f9adeb99387f46e4020fbf3dda885d2b8",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0x638a7aC8315767cEAfc57a6f5e3559454347C3f6",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x297b43ae44660ca7826ef92d8353324c018573ef",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.goerli.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0x9ca1dffc744318198be9cf92283a803ce16b698a",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0x0e563B883dfe11469915194F8651a65212fdB96F",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x5A1584621Ca4d9946630f8586295BC02Ce09bbe7",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "0x5Ed8a1836aa317212cd82138a0f693962DdE0d14",
    },
  },
  [chains.polygon.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0xbe4cad0e99865cc62787ecf029ad9dd4815d3d2e",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0xccA6dd77bA2cfcccEdA01A82CB309e2A17901682",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x576743075fc5f771bbc1376c3267a6185af9d62b",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.arbitrum.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0x9ab73ca73c89af0bdc69642aceb23cc6a55a514c",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0x90Cc23DC3E12E80f27C05b8137b5f0d2b1EdFA20",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0xb7185acaf42c4966ffa3c81486d9ed9633aa4c13",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.avalanche.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0x17167a7e2763121e263b4331b700a1bf9113b387",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0x817fE1364A9d57d1fB951945B53942234163Ef10",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x48b4889cf5d6f8360050f9d7606505f1433120bc",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.bsc.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0xc43b2d8cedb71df30f45dfd9a21ec1e50a813bd6",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0xc9bf2A6bD467A813908d836c1506efE61E465761",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x135e78b8e17b1d189af75fcfcc018ab2e6c7b879",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.gnosis.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0xc84f0e95815a576171a19eb9e0fa55a217ab1536",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0x89AfE038714e547C29Fa881029DD4B5CFB008454",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x5b144c3b9c8cfd48297aeb59b90a024ef3fcce92",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.optimism.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0x77c8516b1f327890c956bb38f93ac2d6b24795ea",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0x8a6974c162fdc7Cb67996F7dB8bAAFb9a99566e0",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x194ed7d6005c8ba4084a948406545df299ad37cd",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.base.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0x50e8b9dc7f28e5ca9253759455c1077e497c4232",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0xf19576Ab425753816eCbF98aca8132A0f693aEc5",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x0648c80b969501c7778b6ff3ba47abb78feedf39",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
  [chains.scroll.chainId]: {
    plugin: {
      alias: "plugin", // SablierV2ProxyPlugin
      address: "0xed1591bd6038032a74d786a452a23536b3201490",
    },
    permit2: {
      alias: "permit2", // Permit2
      address: "0x000000000022d473030f116ddee9f6b43ac78ba3",
    },
    registry: {
      alias: "registry", // PRBProxyRegistry
      address: "0x584009e9ede26e212182c9745f5c000191296a78",
    },
    targetApprove: {
      alias: "targetApprove", // SablierV2ProxyTargetAllowance
      address: "0x71CeA9c4d15fed2E58785cE0C05165CE34313A74",
    },
    targetPermit2: {
      alias: "targetPermit2", // SablierV2ProxyTarget
      address: "0x91154fc80933d25793e6b4d7ce19fb51de6794b7",
    },
    targetWithdrawer: {
      alias: "targetWithdrawer", // PRBProxyWithdrawerTarget
      address: "", // TODO
    },
  },
} as const;

export { contracts, peripheries };
